import { PlanetSkill, PlanetSkillUpgrade } from '@apeiron/planet'
import { DEFAULT_ANIMATE_DURATION } from '@apeiron/temp'
import {
  Box,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'
import ColorizedTypography from '@src/components/share/generic/ColorizedTypography'
import { SkillColorizeMapping } from '@src/constants/layout'
import { useSkillContext } from '@src/contexts/skill/SkillContext'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { FC, MouseEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

const MainContainer = styled(Box)`
  display: flex;
  flex-direction: column;
`

const ButtonTab = styled(ToggleButtonGroup)`
  margin-top: 10px;
  position: relative;
  height: 30px;
  gap: 10px;
  border-radius: unset;
  .MuiToggleButtonGroup-grouped:not(:last-of-type),
  .MuiToggleButtonGroup-grouped:not(:first-of-type) {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`

const Button = styled(ToggleButton)`
  display: flex;
  overflow: hidden;
  white-space: nowrap;
  transition: ${DEFAULT_ANIMATE_DURATION}ms;
  position: relative;
  background-color: #757575;
  text-transform: none;
  ${props => props.theme.breakpoints.up('res1024')} {
    padding: 8px 12px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    padding: 2px 4px;
  }
  p,
  img {
    filter: invert(1) brightness(0.8);
    color: black;
    font-weight: 700;
    font-size: 12px;
  }
  :hover {
    background-color: #9d9d9d;
  }
  &.Mui-selected {
    background-color: #ffbf36;
    p,
    img {
      filter: unset;
      color: black;
      font-size: 12px;
    }
    :hover {
      background-color: #ffce63;
    }
  }
`

const Description = styled(ColorizedTypography)`
  color: #ffffff;
  font-size: 12px;
  font-weight: 300;
  line-height: 18px;
  margin: 10px 0px 20px 0px;
`

const SkillUpgradeDescription: FC<Props> = (props: Props) => {
  const { className, skill } = props

  const { t } = useTranslation()

  const { showDetail, skillGlossary } = useSkillContext()

  const [skillLevel, setSkillLevel] = useState(1)

  const handleOnChange = useCallback((event: any, value: number) => {
    if (R.isNotNil(value)) {
      setSkillLevel(value)
    }
  }, [])

  const handleOnClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
  }, [])

  const handleOnCheckGlossary = useCallback(
    (searchKey: string): string | null => {
      return R.propOr(null, R.toLower(searchKey), skillGlossary)
    },
    [skillGlossary]
  )

  const description = showDetail
    ? R.path(['skillUpgrade', skillLevel - 1, 'description'], skill) ||
      R.path(['skillUpgrade', skillLevel - 1, 'descriptionShort'], skill) ||
      ''
    : R.path(['skillUpgrade', skillLevel - 1, 'descriptionShort'], skill) ||
      R.path(['skillUpgrade', skillLevel - 1, 'description'], skill) ||
      ''

  return (
    <MainContainer className={className}>
      {R.isNotNil(skill?.skillUpgrade) && (
        <>
          <ButtonTab
            className='Custom-Button-Tab'
            value={skillLevel}
            exclusive
            onChange={handleOnChange}
          >
            {RA.mapIndexed((_: PlanetSkillUpgrade, index: number) => {
              return (
                <Button
                  className='Custom-Button'
                  key={`upgrade_${index}`}
                  onClick={handleOnClick}
                  value={index + 1}
                >
                  <Typography>
                    {t('planet.detail.skill_upgrade', { count: index + 1 })}
                  </Typography>
                </Button>
              )
            }, skill.skillUpgrade)}
          </ButtonTab>
          <Description
            className='Custom-Description'
            colorizeMap={SkillColorizeMapping}
            text={description}
            checkDictionary={handleOnCheckGlossary}
          />
        </>
      )}
    </MainContainer>
  )
}

type Props = {
  className?: string
  skill: PlanetSkill
}

export default SkillUpgradeDescription
